// libraries.
import { useEffect, useRef } from "react";
import { Form, Row, Col, InputGroup, Dropdown } from "react-bootstrap";
import { UseFormRegister, UseFormWatch, UseFormSetValue } from "react-hook-form";

// interfaces.
import { RawTool } from "@store/apiSlices/incentiveRules/interfaces/rawRules.interfaces";

// types.
import { FormValues } from "./useCreateRuleForm";

// constants.
import { BASE_PARAMS, CONDITION_OPERATORS, CONTEXT_VARIABLES, ParamOption } from "../rulesFormConstants";

// ─── Types ────────────────────────────────────────────────────────────────────

type ContextVar = ParamOption;

// ─── Helpers ─────────────────────────────────────────────────────────────────

const suggestedToOptions = (
  suggested: (string | number)[],
  contextVars: ContextVar[],
): ContextVar[] =>
  suggested.map((v) => {
    const str = String(v);
    if (typeof v === "string" && v.startsWith("${")) {
      const found =
        contextVars.find((c) => c.value === v) ??
        CONTEXT_VARIABLES.find((c) => c.value === v);
      return found ?? { value: str, label: str.replace(/(?:^\$\{|\}$)/g, "") };
    }
    return { value: str, label: str };
  });

// ─── ComboParamInput ─────────────────────────────────────────────────────────
// Input de texto libre + dropdown ▾ que inserta una variable al hacer clic.

interface ComboParamInputProps {
  idx: number;
  paramKey: string;
  placeholder: string;
  title: string;
  required: boolean;
  options: ContextVar[];
  previousOutputs: string[];
  register: UseFormRegister<FormValues>;
  setValue: UseFormSetValue<FormValues>;
}

const ComboParamInput = ({
  idx,
  paramKey,
  placeholder,
  title,
  required,
  options,
  previousOutputs,
  register,
  setValue,
}: ComboParamInputProps) => {
  const fieldName = `actions.${idx}.params.${paramKey}` as const;
  const hasAnything = options.length > 0 || previousOutputs.length > 0;

  return (
    <InputGroup size="sm">
      <Form.Control
        placeholder={placeholder}
        title={title}
        style={{ borderRight: hasAnything ? 0 : undefined }}
        {...register(fieldName, { required: required ? "Requerido" : false })}
      />
      {hasAnything && (
        <Dropdown align="end">
          <Dropdown.Toggle
            variant="outline-secondary"
            size="sm"
            id={`combo-${idx}-${paramKey}`}
            className="combo-toggle"
            style={{ fontSize: 11, padding: "0 8px", borderLeft: 0 }}
            title="Seleccionar variable"
          >
            ▾
          </Dropdown.Toggle>
          <Dropdown.Menu
            style={{ fontSize: 12, maxHeight: 220, overflowY: "auto", minWidth: 270 }}
          >
            {options.length > 0 && (
              <>
                <Dropdown.Header style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Opciones disponibles
                </Dropdown.Header>
                {options.map((s) => (
                  <Dropdown.Item
                    key={s.value}
                    onClick={() => setValue(fieldName, s.value)}
                    style={{ padding: "4px 16px" }}
                  >
                    <code style={{ fontSize: 10, color: "#6600cc" }}>{s.value}</code>
                    <span className="text-muted ms-2" style={{ fontSize: 11 }}>{s.label}</span>
                  </Dropdown.Item>
                ))}
              </>
            )}
            {previousOutputs.length > 0 && (
              <>
                <Dropdown.Divider />
                <Dropdown.Header style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Salidas de acciones anteriores
                </Dropdown.Header>
                {previousOutputs.map((out) => (
                  <Dropdown.Item
                    key={out}
                    onClick={() => setValue(fieldName, `\${${out}}`)}
                    style={{ padding: "4px 16px" }}
                  >
                    <code style={{ fontSize: 10, color: "#0077cc" }}>{`\${${out}}`}</code>
                    <span className="text-muted ms-2" style={{ fontSize: 11 }}>→ {out}</span>
                  </Dropdown.Item>
                ))}
              </>
            )}
          </Dropdown.Menu>
        </Dropdown>
      )}
    </InputGroup>
  );
};

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  idx: number;
  register: UseFormRegister<FormValues>;
  watch: UseFormWatch<FormValues>;
  setValue: UseFormSetValue<FormValues>;
  tools: RawTool[];
  contextVars: ContextVar[];
  previousOutputs: string[];
  canRemove: boolean;
  isFirst: boolean;
  isLast: boolean;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const ActionEditor = ({
  idx,
  register,
  watch,
  setValue,
  tools,
  contextVars,
  previousOutputs,
  canRemove,
  isFirst,
  isLast,
  onRemove,
  onMoveUp,
  onMoveDown,
}: Props) => {
  const selectedTool = watch(`actions.${idx}.tool`);
  const selectedOperation = watch(`actions.${idx}.operation`);
  const actionConditionEnabled = watch(`actions.${idx}.actionConditionEnabled`);

  const isValidacion = selectedTool === "validacion";
  const operationLabel = isValidacion ? "Tipo" : "Operación";

  const toolData = tools.find((t) => t.name === selectedTool);

  // When the saved value is not yet available in the catalog (catalog still loading,
  // or the tool/operation name doesn't match exactly), we add a fallback <option> so
  // the <select> can display the stored value immediately. This way the user always
  // sees what the rule had, regardless of catalog state.
  const toolInCatalog = !!toolData;
  const operationInCatalog = !!toolData?.operations.find((op) => op.operation === selectedOperation);

  // Re-sync operation DOM value after the tool's options become available.
  const operationRef = useRef(selectedOperation);
  operationRef.current = selectedOperation;
  useEffect(() => {
    if (!selectedTool) return;
    setValue(`actions.${idx}.operation`, operationRef.current, { shouldDirty: false });
  }, [selectedTool, idx, setValue]);

  const operationData = toolData?.operations.find((op) => op.operation === selectedOperation);
  const paramKeys = Object.keys(operationData?.params ?? {}).filter(
    (key) =>
      key !== "operation" &&
      key !== "type" &&
      !BASE_PARAMS.includes(key) &&
      !operationData?.params[key].is_base,
  );

  return (
    <div className="p-2 rounded" style={{ border: "1px solid #e0e0e0", background: "#fafafa" }}>

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="d-flex justify-content-between align-items-center mb-2">
        <span style={{ fontSize: 12, fontWeight: 600, color: "#666" }}>Acción {idx + 1}</span>
        <div className="d-flex align-items-center gap-1">
          <button
            type="button"
            className="btn-move-item"
            title="Mover arriba"
            onClick={onMoveUp}
            disabled={isFirst}
          >
            ↑
          </button>
          <button
            type="button"
            className="btn-move-item"
            title="Mover abajo"
            onClick={onMoveDown}
            disabled={isLast}
          >
            ↓
          </button>
          {canRemove && (
            <button type="button" className="btn-remove-item" onClick={onRemove}>
              ✕ Eliminar
            </button>
          )}
        </div>
      </div>

      {/* ── Nombre y output ─────────────────────────────────────────────── */}
      <Row className="g-2 mb-2">
        <Col xs={6}>
          <Form.Label style={{ fontSize: 12 }}>Nombre de la acción</Form.Label>
          <Form.Control
            size="sm"
            placeholder="ej: aplicar_bono_2pct"
            {...register(`actions.${idx}.actionName`, { required: "Requerido" })}
          />
        </Col>
        <Col xs={6}>
          <Form.Label style={{ fontSize: 12 }}>Variable de salida (output)</Form.Label>
          <Form.Control
            size="sm"
            placeholder="ej: bono_aplicado"
            {...register(`actions.${idx}.output`)}
          />
          <Form.Text className="text-muted" style={{ fontSize: 10 }}>
            Las acciones siguientes pueden usar <code>{"${nombre}.campo"}</code>
          </Form.Text>
        </Col>
      </Row>

      {/* ── Tool y Operación/Tipo ────────────────────────────────────────── */}
      <Row className="g-2 mb-2">
        <Col xs={6}>
          <Form.Label style={{ fontSize: 12 }}>Tool</Form.Label>
          <Form.Select
            size="sm"
            {...register(`actions.${idx}.tool`, { required: "Requerido" })}
            onChange={(e) => {
              setValue(`actions.${idx}.tool`, e.target.value);
              setValue(`actions.${idx}.operation`, "");
              setValue(`actions.${idx}.params`, {});
            }}
          >
            <option value="">Seleccionar tool...</option>
            {/* Fallback: muestra el valor guardado aunque el catálogo no lo tenga */}
            {selectedTool && !toolInCatalog && (
              <option value={selectedTool}>{selectedTool}</option>
            )}
            {tools.map((t) => (
              <option key={t.name} value={t.name}>
                {t.name} — {t.description}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col xs={6}>
          <Form.Label style={{ fontSize: 12 }}>{operationLabel}</Form.Label>
          <Form.Select
            size="sm"
            {...register(`actions.${idx}.operation`, { required: "Requerido" })}
            onChange={(e) => {
              setValue(`actions.${idx}.operation`, e.target.value);
              setValue(`actions.${idx}.params`, {});
            }}
            disabled={!selectedTool}
          >
            <option value="">Seleccionar {operationLabel.toLowerCase()}...</option>
            {/* Fallback: muestra el valor guardado aunque no esté en el catálogo */}
            {selectedOperation && !operationInCatalog && (
              <option value={selectedOperation}>{selectedOperation}</option>
            )}
            {toolData?.operations.map((op) => (
              <option key={op.operation} value={op.operation}>
                {op.operation} — {op.description}
              </option>
            ))}
          </Form.Select>
        </Col>
      </Row>

      {/* ── Parámetros — combo input ─────────────────────────────────────── */}
      {paramKeys.length > 0 && (
        <div className="mt-1">
          <p style={{ fontSize: 11, color: "#888", marginBottom: 6 }}>
            Parámetros de la operación:
          </p>
          <Row className="g-2">
            {paramKeys.map((paramKey) => {
              const paramMeta = operationData!.params[paramKey];
              const suggested = paramMeta.suggested_values ?? [];
              // Parámetro numérico SIN suggested_values → input libre (ej: min/max en rango)
              const isPlainNumeric =
                (paramMeta.type === "float" || paramMeta.type === "int") &&
                suggested.length === 0;
              // Opciones: suggested_values del backend > fallback contextVars
              const dropdownOptions: ContextVar[] =
                suggested.length > 0 ? suggestedToOptions(suggested, contextVars) : contextVars;

              return (
                <Col xs={6} key={paramKey}>
                  <Form.Label style={{ fontSize: 11 }} title={paramKey}>
                    {paramMeta.description}
                    {paramMeta.required && <span className="text-danger"> *</span>}
                  </Form.Label>

                  {isPlainNumeric ? (
                    <Form.Control
                      size="sm"
                      placeholder="ej: 0.02"
                      title={paramMeta.description}
                      {...register(`actions.${idx}.params.${paramKey}`, {
                        required: paramMeta.required ? "Requerido" : false,
                      })}
                    />
                  ) : (
                    <ComboParamInput
                      idx={idx}
                      paramKey={paramKey}
                      placeholder={isPlainNumeric ? "ej: 0.02" : `ej: \${variable}`}
                      title={paramMeta.description}
                      required={paramMeta.required}
                      options={dropdownOptions}
                      previousOutputs={previousOutputs}
                      register={register}
                      setValue={setValue}
                    />
                  )}

                </Col>
              );
            })}
          </Row>
        </div>
      )}

      {/* ── Condición de acción ─────────────────────────────────────────── */}
      <div className="mt-2 pt-2" style={{ borderTop: "1px dashed #e0e0e0" }}>
        <Form.Check
          type="switch"
          id={`action-condition-${idx}`}
          label={<span style={{ fontSize: 11, color: "#666" }}>Agregar condición a esta acción</span>}
          {...register(`actions.${idx}.actionConditionEnabled`)}
        />

        {actionConditionEnabled && (
          <Row className="g-2 mt-1">
            <Col xs={5}>
              <Form.Select
                size="sm"
                {...register(`actions.${idx}.actionConditionLeft`, { required: "Requerido" })}
              >
                <option value="">Variable...</option>
                {contextVars.map((v) => (
                  <option key={v.value} value={v.value}>
                    {v.label}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={3}>
              <Form.Select size="sm" {...register(`actions.${idx}.actionConditionType`)}>
                {CONDITION_OPERATORS.map((op) => (
                  <option key={op.value} value={op.value}>
                    {op.label}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={4}>
              <Form.Control
                size="sm"
                placeholder="Valor"
                {...register(`actions.${idx}.actionConditionRight`, { required: "Requerido" })}
              />
            </Col>
          </Row>
        )}
      </div>
    </div>
  );
};
